import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  Grid,
  DialogTitle,
  Button,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Box,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { setErrorMessage } from "../../redux/actions/index";
import LoadingSpinner from "../LoadingSpinner";
import SocialLinks from "../SocialLinks";
import URLField from "../URLField";
import { toDate, formatISO, parseISO } from "date-fns";
import {
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
} from "@/services/api";
import SocialProductList from "./social/ProductList";

const URLFields = ({ urls, removeURL, editURL, label }) => {
  if (!Array.isArray(urls)) {
    return null;
  }

  return urls.map((e, i) => (
    <Grid key={i} item xs={12} sm={12} md={12}>
      <FormControl fullWidth variant="outlined">
        <InputLabel
          htmlFor="outlined-adornment-password"
          style={{
            color: "#aeb5bd",
          }}
        >
          {label} {i + 1}
        </InputLabel>
        <URLField index={i} removeURL={removeURL} url={e} editURL={editURL} />
      </FormControl>
    </Grid>
  ));
};

const newEmptyProject = () => ({
  abnumber: "",
  projectType: "abnumberChoice",
  name: "",
  description: "",
  client: "",
  daterangeFrom: new Date(),
  daterangeTo: new Date(),
  urls: [],
  socialLinks: [],
  isLinkoutCampaign: false,
  isContentHub: false,
});

const ProjectForm = ({ project, handleCloseDialog }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAdmin = useSelector((state) => state.isAdmin);

  // Determine mode based on project prop
  const isEditMode = !!project;

  const updateSocialProducts = (products) => {
    setProjectData({ ...projectData, socialProducts: products });
  };

  // Initialize project data
  const [projectData, setProjectData] = useState(
    isEditMode ? project : newEmptyProject(),
  );

  const [projectType, setProjectType] = useState(
    isEditMode
      ? project.projectType === "storyLinkChoice"
        ? "customURL"
        : project.projectType === "soMeAdChoice"
          ? "soMeAd"
          : "default"
      : "default",
  );

  const [includesSocialMediaPosts, setIncludesSocialMediaPosts] = useState(
    isEditMode
      ? (project.socialProducts && project.socialProducts.length > 0) ||
          (project.socialLinks && project.socialLinks.length > 0)
      : false,
  );

  const [includesLongforms, setIncludesLongforms] = useState(
    isEditMode ? project.longforms && project.longforms.length > 0 : false,
  );

  // RTK Query mutations
  const [createProject, { isLoading: isCreating }] = useCreateProjectMutation();
  const [updateProject, { isLoading: isUpdating }] = useUpdateProjectMutation();
  const [deleteProject, { isLoading: isDeleting }] = useDeleteProjectMutation();

  const isLoading = isCreating || isUpdating || isDeleting;

  const handleChange = (event) => {
    const edit = {};

    // trim whitespace from abnumber
    if (event.target.name === "abnumber") {
      const trimmed = event.target.value.replace(" ", "");
      edit[event.target.name] = trimmed;
    } else {
      edit[event.target.name] = event.target.value;
    }
    setProjectData({ ...projectData, ...edit });
  };

  const handleDateChange = (fieldName) => (date) => {
    if (date !== "Invalid Date") {
      const edit = {};
      if (isEditMode) {
        edit[fieldName] = formatISO(date);
      } else {
        edit[fieldName] = date;
      }
      setProjectData({ ...projectData, ...edit });
    }
  };

  const handleIsLinkoutCampaignChange = (value) => {
    const checkValue = value.target.checked;
    const edit = { isLinkoutCampaign: checkValue };
    setProjectData({ ...projectData, ...edit });
  };

  const handleIsContentHubChange = (value) => {
    const checkValue = value.target.checked;
    const edit = { isContentHub: checkValue };
    setProjectData({ ...projectData, ...edit });
  };

  // TODO: Replace this with support for socialProducts
  const handleIncludesSocialMediaPostsChange = (value) => {
    const checked = value.target.checked;
    setIncludesSocialMediaPosts(checked);
  };

  const handleIncludesLongformsChange = (value) => {
    const checked = value.target.checked;
    setIncludesLongforms(checked);
    const longforms = checked ? [""] : [];
    const edit = { longforms };
    setProjectData({ ...projectData, ...edit });
  };

  const handleDailyDeal = (value) => {
    const checkValue = value.target.checked;
    const edit = { isDailyDeal: checkValue };
    setProjectData({ ...projectData, ...edit });
  };

  const addURL = () => {
    const urls = [...(projectData.urls || []), ""];
    setProjectData({ ...projectData, urls });
  };

  const removeURL = (key) => () => {
    const urls = [...(projectData.urls || [])];
    urls.splice(key, 1);
    setProjectData({ ...projectData, urls });
  };

  const editURL = (key) => (value) => {
    const urls = [...(projectData.urls || [])];
    urls[key] = value;
    setProjectData({ ...projectData, urls });
  };

  const removeSocialLink = (key) => () => {
    const socialLinks = [...(projectData.socialLinks || [])];
    socialLinks.splice(key, 1);
    setProjectData({ ...projectData, socialLinks });
  };

  const addLongform = () => {
    const longforms = [...(projectData.longforms || []), ""];
    setProjectData({ ...projectData, longforms });
  };

  const removeLongform = (key) => () => {
    const longforms = [...(projectData.longforms || [])];
    longforms.splice(key, 1);
    setProjectData({ ...projectData, longforms });
  };

  const editLongform = (key) => (value) => {
    const longforms = [...(projectData.longforms || [])];
    longforms[key] = value;
    setProjectData({ ...projectData, longforms });
  };

  const saveProject = async () => {
    dispatch(setErrorMessage(null));

    const data = JSON.parse(JSON.stringify(projectData));
    data.daterangeFrom = moment(projectData.daterangeFrom).format("YYYY-MM-DD");
    data.daterangeTo = moment(projectData.daterangeTo).format("YYYY-MM-DD");

    // check if storyLinkChoice, remove SEO slug if it is
    if (projectData && projectData.projectType === "storyLinkChoice") {
      const validURLPattern = /.*stories\/\d{1,8}/i;
      let urlsNoSEO;
      if (Array.isArray(projectData.urls)) {
        urlsNoSEO = projectData.urls.map((e) => {
          const match = e.match(validURLPattern);
          if (Array.isArray(match)) {
            return match[0];
          }
          return e;
        });
      }
      data.urls = urlsNoSEO;
    }

    if (projectData && projectData.projectType === "soMeAdChoice") {
      delete data.abnumber;
    }

    try {
      if (isEditMode) {
        await updateProject({ patch: data }).unwrap();
        handleCloseDialog();
      } else {
        const result = await createProject(data).unwrap();
        navigate(`/reports/${result._id}`);
      }
    } catch (err) {
      let msg = isEditMode
        ? "Error updating project"
        : "Error creating project";
      if (err.data) {
        msg += `: ${err.data}`;
      }
      dispatch(setErrorMessage(msg));
    }
  };

  const handleDeleteClick = async () => {
    dispatch(setErrorMessage(null));

    try {
      await deleteProject(projectData._id).unwrap();
      handleCloseDialog();
      navigate("/");
    } catch (err) {
      let msg = "Error deleting project";
      if (err.data) {
        msg += `: ${err.data}`;
      }
      dispatch(setErrorMessage(msg));
    }
  };

  useEffect(() => {
    let edit = {};
    switch (projectType) {
      case "customURL":
        edit = { projectType: "storyLinkChoice" };
        break;
      case "soMeAd":
        edit = { projectType: "soMeAdChoice" };
        break;
      default:
        edit = { projectType: "abnumberChoice" };
    }
    setProjectData({ ...projectData, ...edit });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectType]);

  const changeType = (newType) => {
    setProjectType(newType);
    if (
      projectData &&
      Array.isArray(projectData.urls) &&
      projectData.urls.length === 0
    ) {
      addURL();
    }
    // TODO: do we need this for social products?
    // if (
    //   projectData &&
    //   Array.isArray(projectData.socialLinks) &&
    //   projectData.socialLinks.length === 0
    // ) {
    //   addSocialLink();
    // }
  };

  // Date value handling for DatePicker
  const getDateValue = (dateField) => {
    if (isEditMode && typeof dateField === "string") {
      return parseISO(dateField);
    } else if (typeof dateField === "string") {
      return parseISO(dateField);
    }
    return toDate(dateField || new Date());
  };

  return (
    <Grid container spacing={2}>
      <DialogTitle>
        {isEditMode ? "Edit" : "Create"} {projectType === "default" && ""}
        {projectType === "customURL" && "Custom URL"}
        {projectType === "soMeAd" && "SoMeAd Only"} Report
      </DialogTitle>

      <Grid item xs={12} sm={12} md={12}>
        <Grid container spacing={2}>
          {projectType === "default" && (
            <Grid item xs={12} sm={12} md={12}>
              <TextField
                name="abnumber"
                variant="outlined"
                label="Order number"
                value={projectData.abnumber || ""}
                fullWidth
                onChange={handleChange}
                InputLabelProps={{
                  style: {
                    color: "#aeb5bd",
                  },
                }}
              />
            </Grid>
          )}

          {(projectType !== "default" || isEditMode) && (
            <Grid item xs={12} sm={12} md={12}>
              <TextField
                label="Customer"
                name="client"
                variant="outlined"
                value={projectData.client || ""}
                fullWidth
                onChange={handleChange}
                InputLabelProps={{
                  style: {
                    color: "#aeb5bd",
                  },
                }}
              />
            </Grid>
          )}

          {projectType !== "default" && (
            <Grid item xs={12} sm={12} md={12}>
              <TextField
                name="name"
                variant="outlined"
                label="Campaign"
                value={projectData.name || ""}
                fullWidth
                onChange={handleChange}
                InputLabelProps={{
                  style: {
                    color: "#aeb5bd",
                  },
                }}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={12} md={6}>
            <DatePicker
              label="Start date"
              value={getDateValue(projectData.daterangeFrom)}
              onChange={handleDateChange("daterangeFrom")}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
              slots={{
                openPickerIcon: () => (
                  <img
                    src="/img/icons/calendar-grey.svg"
                    alt="Pick from date"
                    style={{ width: "28px", height: "28px" }}
                  />
                ),
              }}
              minDate={
                isAdmin ? new Date("2020-01-01") : new Date("2021-01-01")
              }
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <DatePicker
              label="End date"
              value={getDateValue(projectData.daterangeTo)}
              onChange={handleDateChange("daterangeTo")}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
              slots={{
                openPickerIcon: () => (
                  <img
                    src="/img/icons/calendar-grey.svg"
                    alt="Pick from date"
                    style={{ width: "28px", height: "28px" }}
                  />
                ),
              }}
              minDate={
                isAdmin ? new Date("2020-01-01") : new Date("2021-01-01")
              }
            />
          </Grid>

          {projectType === "default" && (
            <>
              <Grid item xs={12} sm={12} md={6}>
                <FormControlLabel
                  label="Linkout Campaign"
                  control={
                    <Checkbox
                      checked={projectData.isLinkoutCampaign}
                      onChange={handleIsLinkoutCampaignChange}
                    />
                  }
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6}>
                <FormControlLabel
                  label="Content Hub"
                  control={
                    <Checkbox
                      checked={projectData.isContentHub}
                      onChange={handleIsContentHubChange}
                    />
                  }
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6}>
                <FormControlLabel
                  label="Includes Social Media Posts"
                  control={
                    <Checkbox
                      checked={includesSocialMediaPosts}
                      onChange={handleIncludesSocialMediaPostsChange}
                    />
                  }
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6}>
                <FormControlLabel
                  label="Includes Longforms"
                  control={
                    <Checkbox
                      checked={includesLongforms}
                      onChange={handleIncludesLongformsChange}
                    />
                  }
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6}>
                <FormControlLabel
                  value="isDailyDeal"
                  label="Daily Deal"
                  labelPlacement="end"
                  control={
                    <Checkbox
                      onChange={handleDailyDeal}
                      checked={projectData.isDailyDeal}
                    />
                  }
                />
              </Grid>
            </>
          )}

          {projectType === "customURL" && (
            <>
              <URLFields
                urls={projectData.urls}
                removeURL={removeURL}
                editURL={editURL}
                label="Story Link"
              />
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                style={{ display: "flex", justifyContent: "flex-end" }}
              >
                <IconButton onClick={addURL} size="large">
                  <Box style={{ display: "flex" }}>
                    <img
                      src="/img/icons/plus-circle.svg"
                      alt="Add URL"
                      style={{ width: "23.6px", height: "23.6px" }}
                    />
                  </Box>
                </IconButton>
              </Grid>
            </>
          )}

          {/* MOVE INTO SUB COMPONENT */}
          {(projectType === "soMeAd" ||
            (projectType === "default" && includesSocialMediaPosts)) && (
            <>
              <SocialProductList
                socialProducts={projectData.socialProducts || []}
                onProductsChange={(e) => updateSocialProducts(e)}
              />
              <SocialLinks
                socialLinks={projectData.socialLinks}
                removeSocialLink={removeSocialLink}
                label="Social Media Post"
              />
            </>
          )}

          {projectType === "default" && includesLongforms && (
            <>
              <URLFields
                urls={projectData.longforms}
                removeURL={removeLongform}
                editURL={editLongform}
                label="Longform URL"
              />
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                style={{ display: "flex", justifyContent: "flex-end" }}
              >
                <IconButton onClick={addLongform} size="large">
                  <Box style={{ display: "flex" }}>
                    <img
                      src="/img/icons/plus-circle.svg"
                      alt="Add longform"
                      style={{ width: "23.6px", height: "23.6px" }}
                    />
                  </Box>
                </IconButton>
              </Grid>
            </>
          )}

          <LoadingSpinner isLoading={isLoading} />

          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            {isEditMode && isAdmin && (
              <Box component="span" m={1}>
                <Button
                  variant="outlined"
                  onClick={handleDeleteClick}
                  style={{
                    textTransform: "none",
                    color: "white",
                    minHeight: "47px",
                    minWidth: "163px",
                    backgroundColor: "#223647",
                    fontSize: "16px",
                    fontWeight: "700",
                    "&:hover": {
                      backgroundColor: "rgba(34, 54, 71, 0.76)",
                    },
                  }}
                >
                  Delete
                </Button>
              </Box>
            )}
            <Box component="span" m={1}>
              <Button
                variant="outlined"
                onClick={saveProject}
                style={{
                  textTransform: "none",
                  color: "white",
                  minHeight: "47px",
                  minWidth: "163px",
                  backgroundColor: "#223647",
                  fontSize: "16px",
                  fontWeight: "700",
                  "&:hover": {
                    backgroundColor: "rgba(34, 54, 71, 0.76)",
                  },
                }}
              >
                Save
              </Button>
            </Box>
          </Grid>

          {!isEditMode && (
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              {projectType !== "default" && (
                <Box
                  m={1}
                  onClick={() => changeType("default")}
                  style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontWeight: "200",
                    fontSize: "13px",
                    fontFamily: "BatonTurbo-Book",
                  }}
                >
                  Automatic (Order number)
                </Box>
              )}
              {projectType !== "customURL" && (
                <Box
                  m={1}
                  onClick={() => changeType("customURL")}
                  style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontWeight: "200",
                    fontSize: "13px",
                    fontFamily: "BatonTurbo-Book",
                  }}
                >
                  Custom URL
                </Box>
              )}
              {projectType !== "soMeAd" && (
                <Box
                  m={1}
                  onClick={() => changeType("soMeAd")}
                  style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontWeight: "200",
                    fontSize: "13px",
                    fontFamily: "BatonTurbo-Book",
                  }}
                >
                  Social Media Only
                </Box>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ProjectForm;
